import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { matchesAPI, playersAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { formatDuration, getStatusColor } from '../lib/utils';
import {
  Play,
  Pause,
  RotateCcw,
  AlertTriangle,
  Clock,
  Share2,
  CheckCircle,
  ArrowLeftRight,
  Phone,
  Mail,
} from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_3ec18717-9700-48f1-a5a2-71ac0edd3a6f/artifacts/29waugc4_WhatsApp%20Image%202026-03-06%20at%2016.26.44.jpeg';

const AD_IMAGE_URL = 'https://customer-assets.emergentagent.com/job_c4d991d4-d014-4f45-b4e8-42cd3f166957/artifacts/9k3pqxmr_LoveAll%20badminton%20logo%20design.png';

// Ad banner that shuffles between logo and contact info
function AdBanner() {
  const [showLogo, setShowLogo] = useState(true);
  const [enabled, setEnabled] = useState(true);

  useEffect(() => {
    const base = (process.env.REACT_APP_BACKEND_URL || '').replace('http://', 'https://');
    fetch(`${base}/api/telemetry/ad-config-public`)
      .then(r => r.ok ? r.json() : null)
      .then(c => { if (c && c.ads_enabled === false) setEnabled(false); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setShowLogo(prev => !prev);
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  if (!enabled) return null;

  return (
    <div className="w-full rounded-lg overflow-hidden border border-border/50 bg-black transition-all duration-700">
      {showLogo ? (
        <div className="flex items-center justify-center py-3 px-4">
          <img src={AD_IMAGE_URL} alt="LoveAll" className="h-16 sm:h-20 w-auto object-contain" />
        </div>
      ) : (
        <div className="flex items-center justify-center py-4 px-6 gap-6">
          <div className="text-center space-y-1">
            <p className="text-sm font-heading font-bold text-white">Contact Us</p>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Mail className="h-3 w-3" /> support@loveall.app
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Phone className="h-3 w-3" /> +91 98765 43210
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function MatchScoring() {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [match, setMatch] = useState(null);
  const [players, setPlayers] = useState({});
  const [elapsedTime, setElapsedTime] = useState(0);
  const [updating, setUpdating] = useState(false);
  const [swapped, setSwapped] = useState(false);

  const fetchMatch = useCallback(async () => {
    try {
      // Match fetch is the only required call. Players lookup is best-effort
      // — if it fails we still render the match with raw IDs as fallback so
      // anonymous spectators on a share link aren't bounced away.
      const matchRes = await matchesAPI.getById(matchId);
      setMatch(matchRes.data);

      try {
        const playersRes = await playersAPI.getAll();
        const playersLookup = {};
        (playersRes.data || []).forEach((p) => {
          playersLookup[p.user_id] = p;
        });
        setPlayers(playersLookup);
      } catch { /* keep players empty — IDs will render as fallback names */ }

      // Calculate elapsed time
      if (matchRes.data.started_at) {
        const started = new Date(matchRes.data.started_at);
        const now = new Date();
        setElapsedTime(Math.floor((now - started) / 1000));
      }
    } catch (error) {
      toast.error('Failed to load match');
      // Anonymous viewers should go home, not be bounced into the owner-login
      // wall via the protected /matches list.
      navigate(isAuthenticated ? '/matches' : '/');
    } finally {
      setLoading(false);
    }
  }, [matchId, navigate, isAuthenticated]);

  useEffect(() => {
    fetchMatch();
  }, [fetchMatch]);

  // Auto-refresh live match every 30 seconds
  useEffect(() => {
    if (match?.status === 'live') {
      const refreshInterval = setInterval(fetchMatch, 30000);
      return () => clearInterval(refreshInterval);
    }
  }, [match?.status, fetchMatch]);

  // Timer for live match
  useEffect(() => {
    if (match?.status === 'live') {
      const timer = setInterval(() => {
        setElapsedTime((prev) => prev + 1);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [match?.status]);

  const handleStartMatch = async () => {
    setUpdating(true);
    try {
      await matchesAPI.start(matchId);
      await fetchMatch();
      toast.success('Match started!');
    } catch (error) {
      toast.error('Failed to start match');
    } finally {
      setUpdating(false);
    }
  };

  const handleScore = async (scoringPlayer, isFault = false, isLet = false) => {
    if (match?.status !== 'live') {
      toast.error('Match must be live to score');
      return;
    }

    setUpdating(true);
    try {
      const response = await matchesAPI.updateScore(matchId, {
        scoring_player: scoringPlayer,
        is_fault: isFault,
        is_let: isLet,
      });
      
      await fetchMatch();
      
      if (response.data.message?.includes('completed')) {
        toast.success(`Match completed! Winner: ${scoringPlayer === 'player1' ? getPlayerName(match.player1_id) : getPlayerName(match.player2_id)}`);
      } else if (response.data.game_winner) {
        toast.info(`Game ${response.data.game_winner === 'player1' ? '1' : '2'} won! Starting next game.`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update score');
    } finally {
      setUpdating(false);
    }
  };

  const handleLet = async () => {
    await handleScore('player1', false, true);
  };

  const getPlayerName = (playerId) => {
    return players[playerId]?.name || 'Player';
  };

  const getCurrentGame = () => {
    if (!match?.games || match.games.length === 0) return { player1_score: 0, player2_score: 0 };
    return match.games[match.current_game - 1] || { player1_score: 0, player2_score: 0 };
  };

  const getGamesWon = (player) => {
    if (!match?.games) return 0;
    return match.games.filter((g) => g.winner === player).length;
  };

  const handleShare = async () => {
    const shareUrl = window.location.href;
    const shareText = `Watch live badminton match: ${getPlayerName(match.player1_id)} vs ${getPlayerName(match.player2_id)}`;
    
    if (navigator.share) {
      try {
        await navigator.share({ title: 'LoveAll Match', text: shareText, url: shareUrl });
      } catch (error) {
        // User cancelled share dialog — expected, log at debug level only.
        if (process.env.NODE_ENV !== 'production') console.debug('share cancelled:', error?.message);
      }
    } else {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Match link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-64 w-full" />
          <div className="grid grid-cols-2 gap-4">
            <Skeleton className="h-32" />
            <Skeleton className="h-32" />
          </div>
        </div>
      </Layout>
    );
  }

  if (!match) return null;

  const currentGame = getCurrentGame();
  const isCompleted = match.status === 'completed';
  const isLive = match.status === 'live';
  const isScheduled = match.status === 'scheduled';
  const isPendingConfirmation = match.confirmation_status === 'pending';
  const isDeclined = match.confirmation_status === 'declined';

  const handleConfirmMatch = async () => {
    try {
      await matchesAPI.confirm(matchId);
      toast.success('Match confirmed!');
      fetchMatch();
    } catch (err) {
      console.error('Confirm match failed:', err);
      toast.error('Failed to confirm');
    }
  };

  const handleDeclineMatch = async () => {
    try {
      await matchesAPI.decline(matchId);
      toast.info('Match declined');
      fetchMatch();
    } catch (err) {
      console.error('Decline match failed:', err);
      toast.error('Failed to decline');
    }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="match-scoring-page">
        {/* Match Confirmation Banner */}
        {isPendingConfirmation && isScheduled && isAuthenticated && (
          <div className="rounded-lg border-2 border-yellow-500/50 bg-yellow-500/10 p-4 flex items-center justify-between">
            <div>
              <p className="font-medium text-sm">Waiting for opponent confirmation</p>
              <p className="text-xs text-muted-foreground">The opponent needs to accept this match before it can start.</p>
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleConfirmMatch} className="bg-green-600 hover:bg-green-700" data-testid="confirm-match-btn">Accept</Button>
              <Button size="sm" variant="outline" onClick={handleDeclineMatch} className="border-red-500 text-red-500 hover:bg-red-500/10" data-testid="decline-match-btn">Decline</Button>
            </div>
          </div>
        )}
        {isDeclined && (
          <div className="rounded-lg border border-red-500/50 bg-red-500/10 p-4 text-center">
            <p className="text-sm text-red-400">This match was declined by the opponent.</p>
          </div>
        )}
        {/* Match Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Badge className={`${getStatusColor(match.status)} text-sm px-4 py-2`}>
              {isLive && <span className="h-2 w-2 rounded-full bg-white mr-2 live-indicator"></span>}
              {match.status.toUpperCase()}
            </Badge>
            <div className="flex items-center gap-2 text-muted-foreground">
              <Clock className="h-4 w-4" />
              <span className="font-mono">{formatDuration(isLive ? elapsedTime : match.duration_seconds)}</span>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setSwapped(!swapped)} data-testid="swap-players-btn" title="Swap player sides">
              <ArrowLeftRight className="mr-2 h-4 w-4" />
              Swap
            </Button>
            <Button variant="outline" onClick={handleShare} data-testid="share-btn">
              <Share2 className="mr-2 h-4 w-4" />
              Share
            </Button>
            {isLive && (
              <Button variant="outline" onClick={handleLet} disabled={updating} data-testid="let-btn">
                <RotateCcw className="mr-2 h-4 w-4" />
                Let
              </Button>
            )}
          </div>
        </div>

        {/* Score Display */}
        <Card className="overflow-hidden">
          <div className="bg-slate-900 p-8">
            {/* Games Won */}
            <div className="flex justify-center gap-8 mb-6">
              <div className="text-center">
                <p className="text-slate-400 text-sm mb-2">Games Won</p>
                <div className="flex gap-2">
                  {[...Array(Math.ceil(match.best_of / 2) + 1)].map((_, i) => (
                    <div
                      key={`p1-${i}`}
                      className={`w-4 h-4 rounded-full ${
                        i < getGamesWon('player1') ? 'bg-primary' : 'bg-slate-700'
                      }`}
                    />
                  ))}
                </div>
              </div>
              <div className="text-center">
                <p className="text-slate-400 text-sm mb-2">Games Won</p>
                <div className="flex gap-2">
                  {[...Array(Math.ceil(match.best_of / 2) + 1)].map((_, i) => (
                    <div
                      key={`p2-${i}`}
                      className={`w-4 h-4 rounded-full ${
                        i < getGamesWon('player2') ? 'bg-secondary' : 'bg-slate-700'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Main Score */}
            <div className="grid grid-cols-3 items-center gap-4">
              {/* Left Player (P1 or P2 if swapped) */}
              <div className="text-center">
                <p className="text-white font-heading text-xl font-bold mb-1">
                  {swapped ? getPlayerName(match.player2_id) : getPlayerName(match.player1_id)}
                </p>
                {!swapped && match.player1_partner_id && (
                  <p className="text-white font-heading text-xl font-bold">& {getPlayerName(match.player1_partner_id)}</p>
                )}
                {swapped && match.player2_partner_id && (
                  <p className="text-white font-heading text-xl font-bold">& {getPlayerName(match.player2_partner_id)}</p>
                )}
                {((swapped ? match.player2_id : match.player1_id) === match.current_server) && isLive && (
                  <div className="flex justify-center mt-2">
                    <div className="service-indicator" title="Serving" />
                  </div>
                )}
              </div>

              {/* Score */}
              <div className="text-center">
                <div className="flex items-center justify-center gap-4">
                  <span className="font-heading text-7xl md:text-8xl font-black text-primary tabular-nums">
                    {swapped ? currentGame.player2_score : currentGame.player1_score}
                  </span>
                  <span className="text-slate-500 text-4xl">-</span>
                  <span className="font-heading text-7xl md:text-8xl font-black text-secondary tabular-nums">
                    {swapped ? currentGame.player1_score : currentGame.player2_score}
                  </span>
                </div>
                {isLive && (
                  <p className="text-slate-400 mt-4">
                    Game {match.current_game} • Service: {match.service_side === 'right' ? 'Right' : 'Left'}
                  </p>
                )}
              </div>

              {/* Right Player */}
              <div className="text-center">
                <p className="text-white font-heading text-xl font-bold mb-1">
                  {swapped ? getPlayerName(match.player1_id) : getPlayerName(match.player2_id)}
                </p>
                {!swapped && match.player2_partner_id && (
                  <p className="text-white font-heading text-xl font-bold">& {getPlayerName(match.player2_partner_id)}</p>
                )}
                {swapped && match.player1_partner_id && (
                  <p className="text-white font-heading text-xl font-bold">& {getPlayerName(match.player1_partner_id)}</p>
                )}
                {((swapped ? match.player1_id : match.player2_id) === match.current_server) && isLive && (
                  <div className="flex justify-center mt-2">
                    <div className="service-indicator" title="Serving" />
                  </div>
                )}
              </div>
            </div>

            {/* Game History */}
            {match.games.length > 1 && (
              <div className="mt-6 flex justify-center gap-4">
                {match.games.map((game, idx) => (
                  <div
                    key={`game-${idx}-${game.player1_score}-${game.player2_score}`}
                    className={`px-4 py-2 rounded ${
                      idx === match.current_game - 1 ? 'bg-slate-700' : 'bg-slate-800'
                    }`}
                  >
                    <p className="text-xs text-slate-400 mb-1">Game {idx + 1}</p>
                    <p className="font-mono text-white">
                      {game.player1_score}-{game.player2_score}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>

        {/* Score Controls — only for authenticated users */}
        {!isCompleted && isAuthenticated && (
          <div className="space-y-4">
            {isScheduled && (
              <Button
                className="w-full h-16 text-xl font-bold bg-secondary hover:bg-secondary/90"
                onClick={handleStartMatch}
                disabled={updating}
                data-testid="start-match-btn"
              >
                <Play className="mr-2 h-6 w-6" />
                Start Match
              </Button>
            )}

            {isLive && (
              <div className="grid grid-cols-2 gap-4">
                <Button
                  className="h-32 text-2xl font-heading font-bold bg-primary hover:bg-primary/90 score-btn"
                  onClick={() => handleScore(swapped ? 'player2' : 'player1')}
                  disabled={updating}
                  data-testid="score-player1-btn"
                >
                  +1 {swapped ? getPlayerName(match.player2_id).split(' ')[0] : getPlayerName(match.player1_id).split(' ')[0]}
                </Button>
                <Button
                  className="h-32 text-2xl font-heading font-bold bg-secondary hover:bg-secondary/90 score-btn"
                  onClick={() => handleScore(swapped ? 'player1' : 'player2')}
                  disabled={updating}
                  data-testid="score-player2-btn"
                >
                  +1 {swapped ? getPlayerName(match.player1_id).split(' ')[0] : getPlayerName(match.player2_id).split(' ')[0]}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Match Completed */}
        {isCompleted && (
          <>
          <Card className="border-green-500/50 bg-green-500/5">
            <CardContent className="py-8 text-center">
              <CheckCircle className="h-16 w-16 mx-auto mb-4 text-green-500" />
              <h2 className="font-heading text-2xl font-bold mb-2">Match Completed!</h2>
              <p className="text-lg text-muted-foreground">
                Winner: <span className="text-green-500 font-bold">
                  {getPlayerName(match.winner_id)}
                  {match.match_type === 'doubles' && match.winner_id === match.player1_id && match.player1_partner_id && (
                    <> & {getPlayerName(match.player1_partner_id)}</>
                  )}
                  {match.match_type === 'doubles' && match.winner_id === match.player2_id && match.player2_partner_id && (
                    <> & {getPlayerName(match.player2_partner_id)}</>
                  )}
                </span>
              </p>
              <p className="text-muted-foreground mt-2">
                Duration: {formatDuration(match.duration_seconds)}
              </p>
              <Button
                className="mt-6"
                onClick={() => navigate(isAuthenticated ? '/matches' : '/')}
                data-testid="match-back-btn"
              >
                {isAuthenticated ? 'Back to Matches' : 'Back to Quick Play'}
              </Button>
            </CardContent>
          </Card>
          </>
        )}
        {match.games?.length > 1 && (
          <Card>
            <CardHeader>
              <CardTitle className="font-heading uppercase tracking-tight text-base">Game-by-Game History</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {match.games.map((game, idx) => {
                  const p1Score = game.player1_score || 0;
                  const p2Score = game.player2_score || 0;
                  const p1Won = game.winner === 'player1';
                  const p2Won = game.winner === 'player2';
                  const p1Width = Math.max(5, Math.round((p1Score / Math.max(p1Score + p2Score, 1)) * 100));
                  return (
                    <div key={`hist-${idx}-${game.player1_score}-${game.player2_score}`} className="flex items-center gap-3" data-testid={`game-history-${idx}`}>
                      <span className="text-xs text-muted-foreground w-14 shrink-0">Game {idx + 1}</span>
                      <div className="flex-1 h-8 rounded-full overflow-hidden flex bg-muted">
                        <div className={`h-full flex items-center justify-end pr-2 text-xs font-mono font-bold transition-all ${p1Won ? 'bg-primary text-primary-foreground' : 'bg-primary/30'}`} style={{ width: `${p1Width}%` }}>
                          {p1Score}
                        </div>
                        <div className={`h-full flex items-center justify-start pl-2 text-xs font-mono font-bold transition-all ${p2Won ? 'bg-secondary text-secondary-foreground' : 'bg-secondary/30'}`} style={{ width: `${100 - p1Width}%` }}>
                          {p2Score}
                        </div>
                      </div>
                      {(p1Won || p2Won) && <Badge variant="outline" className="text-[10px] shrink-0">{p1Won ? getPlayerName(match.player1_id).split(' ')[0] : getPlayerName(match.player2_id).split(' ')[0]}</Badge>}
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between text-xs text-muted-foreground mt-3 px-14">
                <span>{getPlayerName(match.player1_id)}</span>
                <span>{getPlayerName(match.player2_id)}</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Match Stats */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading uppercase tracking-tight">Match Stats</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-2xl font-bold text-primary">{match.faults?.player1 || 0}</p>
                <p className="text-sm text-muted-foreground">Faults</p>
              </div>
              <div>
                <p className="text-2xl font-bold">{match.lets || 0}</p>
                <p className="text-sm text-muted-foreground">Lets</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-secondary">{match.faults?.player2 || 0}</p>
                <p className="text-sm text-muted-foreground">Faults</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
